// 弹窗：confirm / prompt 以 Promise 形式调用（二次确认、一句话说明）
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface ConfirmOpts {
  title: string;
  body?: ReactNode;
  okText?: string;
  cancelText?: string;
  danger?: boolean;
}

interface PromptOpts extends ConfirmOpts {
  label?: string;
  placeholder?: string;
  initial?: string;
  required?: boolean;
  multiline?: boolean;
  /** 额外勾选项（如「回滚后自动 dry run」） */
  checkbox?: string;
}

type Pending =
  | { kind: "confirm"; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { kind: "prompt"; opts: PromptOpts; resolve: (v: { text: string; checked: boolean } | null) => void };

interface DialogApi {
  confirm: (o: ConfirmOpts) => Promise<boolean>;
  prompt: (o: PromptOpts) => Promise<string | null>;
  promptEx: (o: PromptOpts) => Promise<{ text: string; checked: boolean } | null>;
}

const Ctx = createContext<DialogApi | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const confirm = useCallback((opts: ConfirmOpts) => new Promise<boolean>((resolve) => setPending({ kind: "confirm", opts, resolve })), []);
  const promptEx = useCallback(
    (opts: PromptOpts) => new Promise<{ text: string; checked: boolean } | null>((resolve) => setPending({ kind: "prompt", opts, resolve })),
    [],
  );
  const prompt = useCallback(async (opts: PromptOpts) => (await promptEx(opts))?.text ?? null, [promptEx]);
  return (
    <Ctx.Provider value={{ confirm, prompt, promptEx }}>
      {children}
      {pending && <DialogView pending={pending} close={() => setPending(null)} />}
    </Ctx.Provider>
  );
}

function DialogView({ pending, close }: { pending: Pending; close: () => void }) {
  const o = pending.opts;
  const p = pending.kind === "prompt" ? pending.opts : null;
  const [text, setText] = useState(p?.initial ?? "");
  const [checked, setChecked] = useState(false);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, []);
  const invalid = !!p?.required && text.trim() === "";
  const cancel = () => {
    if (pending.kind === "confirm") pending.resolve(false);
    else pending.resolve(null);
    close();
  };
  const ok = () => {
    if (pending.kind === "confirm") {
      pending.resolve(true);
      close();
      return;
    }
    setTouched(true);
    if (invalid) return;
    pending.resolve({ text: text.trim(), checked });
    close();
  };
  // 挂到 body 并压在最上层：从别的弹框里再弹确认框时不会被那个弹框盖住
  return createPortal(
    <div className="modal-mask modal-top" onMouseDown={(e) => e.target === e.currentTarget && cancel()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={o.title}>
        <h2>{o.title}</h2>
        {o.body && <div className="stack">{o.body}</div>}
        {p && (
          <label className="field" style={{ marginTop: 14 }}>
            {p.label && <span className="lbl">{p.label}{p.required && <span className="req">*</span>}</span>}
            {p.multiline ? (
              <textarea ref={inputRef} value={text} placeholder={p.placeholder} onChange={(e) => setText(e.target.value)} className={touched && invalid ? "invalid" : ""} />
            ) : (
              <input
                ref={inputRef}
                value={text}
                placeholder={p.placeholder}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ok()}
                className={touched && invalid ? "invalid" : ""}
              />
            )}
            {touched && invalid && <span className="field-err">这一项必须填写</span>}
          </label>
        )}
        {p?.checkbox && (
          <label className="check">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            {p.checkbox}
          </label>
        )}
        <div className="actions">
          <button className="btn" onClick={cancel}>{o.cancelText ?? "返回"}</button>
          <button className={`btn ${o.danger ? "btn-danger-solid" : "btn-primary"}`} onClick={ok}>
            {o.okText ?? "确定"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function useDialog(): DialogApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useDialog 必须在 DialogProvider 里用");
  return v;
}
